import React, { useEffect, useState } from 'react';

import { Button, useDeviceType } from '@braskai/ui-kit';

import { Box, Stack, Typography } from '@mui/material';

import { BookADemoBanner } from '@/features/bookADemoBanner';
import { consumeLandingProgressFloor } from '@/features/landingFlow/model/landingFlowStorage';
import { useTaskExecutionPercent } from '@/features/project/projectState';

import { ProjectTaskType } from '@/entities/project';
import { useCurrentUser } from '@/entities/user/currentUser';

import FullTutorialPreviewImg from '@/shared/assets/images/full-tutorial-preview.png';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { Project } from '@/shared/types/Project';
import { NativeVideoPlayer } from '@/shared/ui/NativeVideoPlayer';

import { GeneratingCard } from './components/GeneratingCard';
import { useEmailConsent } from './hooks/useEmailConsent';
import { TUTORIAL_VIDEO_SRC } from './lib/constants';
import { getTitleByPercent } from './lib/getTitleByPercent';

type TrialProjectProcessingOverrideProps = {
  overridePercent: number;
};

type TrialProjectProcessingProjectProps = {
  isFinalStatus?: boolean;
  status: Project['status'];
  statusStartDate: Project['status_date'];
  taskType: Exclude<ProjectTaskType, 'voiceUpdate'>;
  videoDuration: Project['duration'];
};

type TrialProjectProcessingProps = TrialProjectProcessingOverrideProps | TrialProjectProcessingProjectProps;

const HEADER_OFFSET = '73px';

/** Legacy "Generating video X%" layout — kept for mobile only (desktop uses the redesigned card). */
const MobileProcessingView: React.FC<{ headerOffset?: boolean; percent: number }> = ({ headerOffset, percent }) => {
  const [isPlayed, setIsPlayed] = useState(false);
  const { data: user } = useCurrentUser();
  const { giveEmailConsent, isLoading } = useEmailConsent();

  const handleGiveEmailConsent = async () => {
    MixpanelEvents.trialProcessingEmailConsentClick();
    await giveEmailConsent();
  };

  const handlePlay = () => {
    if (!isPlayed) {
      setIsPlayed(true);
    }
  };

  const hasEmailConsent = user?.email_consent === true;

  const processingSection = (
    <Stack alignItems="center" direction="column" marginTop={40} paddingX={16}>
      <Stack alignItems="center" direction="row" spacing={4}>
        <Typography variant="title-4-md">Generating video</Typography>
        <Typography color="primary.500" variant="title-4-md">
          {percent}%
        </Typography>
      </Stack>

      {hasEmailConsent && (
        <Box marginTop={6} maxWidth={265} textAlign="center">
          <Typography color="grey.500" textAlign="center" variant="body-2-md">
            📥 We'll email you when it's ready, feel free to come back later!
          </Typography>
        </Box>
      )}

      {!hasEmailConsent && (
        <Stack alignItems="center" direction="column" marginTop={16} spacing={16}>
          <Stack alignItems="center" direction="column" spacing={6}>
            <Typography color="grey.900" variant="title-4-md">
              ✉️ No wait, we'll email you when it's ready.
            </Typography>
            <Typography color="grey.500" textAlign="center" variant="body-2-md">
              Only useful info like video notifications special offers and product updates
            </Typography>
          </Stack>

          <Button disabled={isLoading} isLoading={isLoading} variant="primary" onClick={handleGiveEmailConsent}>
            Notify me by email
          </Button>
        </Stack>
      )}
    </Stack>
  );

  const videoSection = (
    <Stack alignItems="center" direction="column" marginTop={16} paddingX={16}>
      <NativeVideoPlayer
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 16,
          maxHeight: 320,
          maxWidth: 580,
          objectFit: 'cover',
        }}
        poster={FullTutorialPreviewImg}
        src={TUTORIAL_VIDEO_SRC}
        autoPlay
        controls
        loop
        onTimeUpdate={handlePlay}
      />
    </Stack>
  );

  return (
    <Box paddingTop={headerOffset ? HEADER_OFFSET : 0}>
      <Box marginTop={10} paddingX={16} width="100%">
        <BookADemoBanner />
      </Box>
      {videoSection}
      {processingSection}
    </Box>
  );
};

const DesktopProcessingView: React.FC<{ headerOffset?: boolean; percent: number }> = ({ headerOffset, percent }) => (
  <Box paddingTop={headerOffset ? HEADER_OFFSET : 0}>
    <Box marginTop={10} paddingX={10} width="100%">
      <BookADemoBanner />
    </Box>
    <GeneratingCard title={getTitleByPercent(percent)} />
  </Box>
);

const TrialProjectProcessingView: React.FC<{ headerOffset?: boolean; percent: number }> = ({
  headerOffset,
  percent,
}) => {
  const { isMobile } = useDeviceType();

  useEffect(() => {
    MixpanelEvents.trialProcessingScreenView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isMobile) {
    return <MobileProcessingView headerOffset={headerOffset} percent={percent} />;
  }

  return <DesktopProcessingView headerOffset={headerOffset} percent={percent} />;
};

const TrialProjectProcessingWithProject: React.FC<TrialProjectProcessingProjectProps> = ({
  isFinalStatus,
  status,
  statusStartDate,
  taskType,
  videoDuration,
}) => {
  const [progressFloor] = useState(() => consumeLandingProgressFloor());
  const progress = useTaskExecutionPercent({
    isFinalStatus,
    status,
    statusStartDate,
    taskType,
    videoDuration,
  });

  const percent = Math.max(progressFloor, progress.percent);

  return <TrialProjectProcessingView percent={percent} />;
};

export const TrialProjectProcessing: React.FC<TrialProjectProcessingProps> = (props) => {
  if ('overridePercent' in props) {
    const { overridePercent } = props;

    return <TrialProjectProcessingView percent={overridePercent} headerOffset />;
  }

  return <TrialProjectProcessingWithProject {...props} />;
};
